"use client"

import { create } from "zustand"
import type { BooruPost } from "@/lib/booru-api"
import { mediaItemsFromBooruPost, type OrderMediaItem } from "@/lib/order-media"

export interface BountySelectedContent {
  id: string
  url: string        // page/reference URL (for display and linking)
  mediaUrl: string   // direct CDN media URL (for editor/player loading)
  thumbnailUrl: string | null
  title: string | null
  type: string
  site: string
  pageCount?: number // for albums/doujins
  mediaItems?: OrderMediaItem[]
}

function getPageUrl(post: BooruPost): string {
  return post.source || post.postUrl || post.fileUrl || post.sampleUrl || ""
}

// post.source/post.postUrl are often the artist's original post page (e.g. a twitter/x.com
// status link), not a direct media file — using them as a media URL fallback silently produces
// an unloadable "image" (the page 403s instead of serving an image). Only fall back to them here
// if they actually look like a direct file.
function looksLikeDirectMediaFile(url: string): boolean {
  return /\.(jpe?g|png|gif|webp|avif|bmp|mp4|webm|mov|m4v)(\?.*)?$/i.test(url)
}

function getMediaUrl(post: BooruPost): string {
  if (post.fileUrl) return post.fileUrl
  if (post.sampleUrl) return post.sampleUrl
  if (post.source && looksLikeDirectMediaFile(post.source)) return post.source
  if (post.postUrl && looksLikeDirectMediaFile(post.postUrl)) return post.postUrl
  return ""
}

function getTitle(post: BooruPost): string | null {
  if (post.title) return post.title
  const tags = post.tags?.filter((tag) => !tag.includes(":") && tag.length > 2).slice(0, 3) ?? []
  return tags.length ? tags.join(", ") : null
}

export function postToBountyContent(post: BooruPost): BountySelectedContent | null {
  const url = getPageUrl(post)
  const isAlbum = post.type === "album"
  // For albums/doujins, mediaUrl should be the gallery page URL so "Open" links to the full gallery
  const mediaUrl = isAlbum ? (url || getMediaUrl(post)) : getMediaUrl(post)
  if (!url && !mediaUrl) return null
  return {
    id: String(post.id ?? ""),
    url: url || mediaUrl,
    mediaUrl: mediaUrl || url,
    thumbnailUrl: post.previewUrl || post.sampleUrl || (isAlbum ? null : post.fileUrl) || null,
    title: getTitle(post),
    type: post.type || "image",
    site: post.site || "unknown",
    pageCount: post.albumMediaCount || undefined,
    mediaItems: mediaItemsFromBooruPost(post),
  }
}

interface BountyContentSelectionState {
  active: boolean
  selectedContent: BountySelectedContent | null
  start: () => void
  cancel: () => void
  selectPost: (post: BooruPost) => BountySelectedContent | null
  consumeSelected: () => BountySelectedContent | null
}

export const useBountyContentSelectionStore = create<BountyContentSelectionState>((set, get) => ({
  active: false,
  selectedContent: null,
  start: () => set({ active: true, selectedContent: null }),
  cancel: () => set({ active: false, selectedContent: null }),
  selectPost: (post) => {
    const content = postToBountyContent(post)
    if (!content) return null
    set({ active: false, selectedContent: content })
    return content
  },
  consumeSelected: () => {
    const selected = get().selectedContent
    if (selected) set({ selectedContent: null })
    return selected
  },
}))
